import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTargetableEntities } from "../useTargetableEntities";
import { getAudienceSegmentTypeFromPath } from "../utils";
import { useDebounce } from "@reactuses/core";
import Select from "react-select";
import FormGroupTile from "../../../basic/FormGroupTile";
import { Flex } from "../../../../../../styled-system/jsx";
import { css } from "../../../../../../styled-system/css";
import { token } from "../../../../../../styled-system/tokens";

const subtitleStyle = css({
  fontWeight: 560,
  fontSize: "1.6rem",
  color: "#73706E",
});

const AudienceMultiplierField = ({
  state,
  onChange,
  errors,
  fieldsToValidate,
  hideTitle = false,
  bulkActionForm = false,
  flexDirection = "column",
  disableFields = false,
  isMediaPlanField = false,
}) => {
  const { t } = useTranslation(["advertising"]);
  const [audienceSearchFilter, setAudienceSearchFilter] = useState("");

  const debouncedAudienceSearchFilter = useDebounce(audienceSearchFilter, 300);
  const getAudienceTargets = useTargetableEntities(debouncedAudienceSearchFilter, state.adType);

  const audienceSearchResults = useMemo(() => {
    return getAudienceTargets.data?.data?.targetableEntities ?? [];
  }, [getAudienceTargets.data?.data?.targetableEntities]);

  const formattedSearchResults = useMemo(() => {
    return audienceSearchResults?.map((targetableEntity) => ({
      label: targetableEntity.audienceResolved,
      value: targetableEntity.audienceId,
      audienceSegmentType: getAudienceSegmentTypeFromPath(targetableEntity.path),
    }));
  }, [audienceSearchResults]);

  useEffect(() => {
    getAudienceTargets.mutate();

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAudienceSearchFilter]);

  return (
    <>
      {!hideTitle ?
        <h3 className={subtitleStyle}>{t(($) => $.advertising["Audience Multiplier:"])}</h3>
      : null}
      <Flex direction={flexDirection} gap={"medium"} style={{ padding: bulkActionForm ? 0 : "0 2rem" }}>
        <div>
          <label
            style={
              bulkActionForm ?
                { color: "black", fontWeight: 600, fontSize: "14px", paddingBottom: "12px" }
              : {
                  color: "#73706E",
                  fontWeight: "600",
                }
            }
          >
            {t(($) => $.advertising["Target Audience"])}
          </label>
          <Select
            type="select"
            stateName={"audienceBidMultiplierTargetAudienceId"}
            inputValue={audienceSearchFilter}
            onInputChange={(value, action) => {
              if (action.action === "input-change") {
                setAudienceSearchFilter(value);
              }
            }}
            isLoading={getAudienceTargets.isPending}
            onChange={(option) => {
              setAudienceSearchFilter("");
              if (!option.value) {
                onChange({
                  audienceBidMultiplierTargetAudienceId: option.value,
                  audienceBidMultiplierTargetAudienceSegmentType: null,
                  audienceBidMultiplier: 0,
                });
                return;
              }

              onChange({
                audienceBidMultiplierTargetAudienceId: option.value,
                audienceBidMultiplierTargetAudienceName: option.label,
                audienceBidMultiplierTargetAudienceSegmentType: option.audienceSegmentType,
              });
            }}
            isDisabled={disableFields}
            value={[
              { label: t(($) => $.advertising["No Audience"]), value: null, audienceSegmentType: null },
              ...formattedSearchResults,
            ].find((audience) => audience.value === state.audienceBidMultiplierTargetAudienceId)}
            options={[
              { label: t(($) => $.advertising["No Audience"]), value: null, audienceSegmentType: null },
              ...formattedSearchResults,
            ]}
            menuPosition="fixed"
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: token("zIndex.dropdown") }),
              container: (base) => ({
                ...base,
                width: bulkActionForm ? "250px" : "100%",
              }),
            }}
          />
        </div>
        <div>
          <label
            style={
              bulkActionForm ?
                { color: "black", fontWeight: 600, fontSize: "14px", paddingBottom: "12px" }
              : {
                  color: "#73706E",
                  fontWeight: "600",
                }
            }
          >
            {t(($) => $.advertising["Multiplier"])}
          </label>
          <div style={{ paddingRight: "2.5rem", width: isMediaPlanField ? "50%" : "200px" }}>
            <FormGroupTile
              type="formcontrol"
              formControlType={"number"}
              prefix={"%"}
              prefixSide={"right"}
              stateName={"audienceBidMultiplier"}
              handleChildFormElement={(key, value) => {
                if (bulkActionForm) {
                  onChange({ [key]: parseInt(value) });
                } else {
                  onChange({ [key]: value });
                }
              }}
              disabled={!state.audienceBidMultiplierTargetAudienceId || disableFields}
              defaultValue={state.audienceBidMultiplier}
            />
          </div>
        </div>

        {errors.audienceBidMultiplier && fieldsToValidate?.includes("audienceBidMultiplier") ?
          <small className="text-danger mt-1 d-block" style={{ paddingTop: "1rem" }}>
            {errors.audienceBidMultiplier}
          </small>
        : null}
      </Flex>
    </>
  );
};

export default AudienceMultiplierField;
